import React, { useRef } from 'react';
import { Box, Card, SxProps, Tab, Tabs, Typography } from '@mui/material';
import { EuroprocApiResponseStatus } from '@europrocurement/l2d-redux-utils';
import { useFlexyTabs } from '@europrocurement/flexy-components/hooks/useFlexyTabs';
import { useL2DTheme } from '@europrocurement/flexy-components/hooks';
import {
    BoxSkeleton,
    SkeletonLoader,
} from '@europrocurement/flexy-components/components/skeletons';
import { TopbarHeight, breadcrumbHeight, footerHeight } from '../../../assets';
import { TabPanel } from '../TabPanel';
import { CountBadge } from '../../atoms/CountBadge';
import { EmptyBox } from '../EmptyBox';
import { Accordion } from '../../organisms/Accordion';

/**
 * Structure définissant un onglet individuel
 */
export type TabStructure = {
    /** Contenu affiché dans l'en-tête de l'onglet */
    tabTitle: React.ReactNode;
    /** Contenu du panneau de l'onglet */
    tabPanel: React.ReactNode;
    /** Indique si l'onglet est désactivé */
    disabled?: boolean;
    /** Identifiant unique de l'onglet, utilisé dans l'URL ou l'état local */
    tabName: string;
    /** Nombre optionnel à afficher à côté du titre (par ex: nombre d'éléments) */
    count?: number;
};

/**
 * Props pour le composant FlexyTabs
 */
export type FlexyTabsProps = {
    /** État de chargement des données à afficher dans les onglets */
    status?: EuroprocApiResponseStatus;
    /** Définition des onglets à afficher */
    tabs: Array<TabStructure>;
    /** Orientation des onglets */
    orientation?: 'vertical' | 'horizontal';
    /** Styles supplémentaires à appliquer au composant */
    sx?: SxProps;
    /** Indique si les onglets doivent être encapsulés dans une carte Material UI */
    inCard?: boolean;
    /** Indique si les onglets doivent être encapsulés dans accordéon */
    inAccordion?: boolean;
    /** Nom du paramètre d'URL utilisé pour persister l'onglet actif */
    routerParameterName?: string;
    /** Indique si le contenu doit avoir une barre de défilement */
    scrollbar?: boolean;
    /** Couleur de l'indicateur de l'onglet actif */
    indicatorColor?: 'primary' | 'secondary';
    /** Utilise un état local plutôt que l'URL pour gérer l'onglet actif */
    useLocalState?: boolean;
    /** Onglet à afficher par défaut en mode d'état local */
    defaultTab?: string;
    /** Callback appelé lors du changement d'onglet en mode d'état local */
    onTabChange?: (tabName: string) => void;
    /** Nom de l'onglet actif en mode d'état local */
    activeTabName?: string;
    /** Rend la barre de navigation des onglets sticky (collée en haut lors du scroll) */
    stickyTabs?: boolean;
    /** Décalage depuis le haut en pixels lorsque stickyTabs est activé (ex: hauteur de la topbar) */
    stickyTop?: number;
    /** Styles supplémentaires pour les onglets du composant */
    tabsSx?: SxProps;
};

/**
 * Composant flexible pour afficher du contenu sous forme d'onglets
 *
 * Prend en charge deux modes de fonctionnement :
 * 1. Mode URL : l'onglet actif est synchronisé avec un paramètre d'URL
 * 2. Mode local : l'onglet actif est géré par un état local
 */
export const FlexyTabs: React.FunctionComponent<FlexyTabsProps> = function ({
    status,
    tabs,
    orientation = 'horizontal',
    sx,
    inCard,
    inAccordion = false,
    routerParameterName = 'tab',
    scrollbar = true,
    indicatorColor = null,
    useLocalState = false,
    defaultTab,
    onTabChange,
    activeTabName,
    stickyTabs = false,
    stickyTop = 0,
    tabsSx = {},
}) {
    /**
     * style
     */
    const { palette } = useL2DTheme();
    // style du wrapper des panels
    const tabWrapperStyle: SxProps = {
        width: '100%',
    };

    // Référence du paneau
    const tabRef = useRef<HTMLDivElement>(null);
    const heightRef = tabRef?.current?.offsetHeight || 0;
    const gap = 15;

    // Détermine la couleur de l'indicateur de l'onglet actif
    const activeTabIndicatorColor = indicatorColor || 'primary';

    // Logique de gestion des onglets extraite dans un hook personnalisé
    const { activeTabIndex, handleTabChange } = useFlexyTabs({
        tabs,
        routerParameterName,
        useLocalState,
        defaultTab,
        onTabChange,
        activeTabName,
    });

    // rend les items de la navigation
    const renderNavigationItem = () =>
        tabs.map(({ tabName, tabTitle, disabled, count }, tabIndex) => {
            // Check if the current tab is activ to display specific style
            const isActivTab = tabIndex === activeTabIndex;

            return (
                <Tab
                    disabled={disabled ?? undefined}
                    aria-disabled={disabled ?? undefined}
                    data-testid={tabTitle}
                    label={
                        <Box display="flex">
                            <Typography
                                variant="l2dbodys"
                                // Update style if activ
                                fontWeight={isActivTab ? 600 : 500}
                                sx={{
                                    color: isActivTab
                                        ? palette.text.primary
                                        : palette.text.secondary,
                                }}
                            >
                                {tabTitle}
                            </Typography>
                            {count && count > 0 && (
                                <Box sx={{ marginX: '10px' }}>
                                    <CountBadge count={count} />
                                </Box>
                            )}
                        </Box>
                    }
                    key={tabName}
                />
            );
        });

    /**
     * Génère la barre de navigation des onglets
     */
    const renderTabNavigation = () => (
        <Tabs
            ref={tabRef}
            value={activeTabIndex}
            indicatorColor={activeTabIndicatorColor}
            onChange={handleTabChange}
            orientation={orientation}
            sx={
                orientation === 'vertical'
                    ? {
                          borderRight: 1,
                          borderColor: 'divider',
                          ...(stickyTabs && {
                              position: 'sticky',
                              top: stickyTop,
                              zIndex: 10,
                              backgroundColor: palette.background.paper,
                          }),
                      }
                    : {
                          borderBottom: 1,
                          borderColor: 'divider',
                          ...(stickyTabs && {
                              position: 'sticky',
                              top: stickyTop,
                              zIndex: 10,
                              backgroundColor: palette.background.paper,
                              px: 2,
                              borderRadius: '0px 0px 12px 12px',
                              boxShadow: `0px 2px 2.5px 0px ${palette.grey[200]}`,
                          }),
                          ...tabsSx,
                      }
            }
        >
            {renderNavigationItem()}
        </Tabs>
    );

    /**
     * Calcule le style du conteneur des panneaux d'onglets
     */
    const getPanelContainerStyle = (): SxProps => {
        if (orientation === 'vertical') {
            return {
                padding: '0px 40px',
                display: 'flex',
                flexDirection: 'column',
                gap: `${gap}px`,
                width: '100%',
            };
        }

        return {
            height: scrollbar
                ? `calc(100vh - ${TopbarHeight}px - ${gap}px - ${heightRef}px - ${footerHeight}px - ${breadcrumbHeight}px)`
                : '100%',
        };
    };

    /**
     * Génère la structure complète des onglets (navigation + contenu)
     */
    const renderTabsStructure = () => {
        const panelContent = (
            <Box sx={getPanelContainerStyle()}>
                <SkeletonLoader
                    skeleton={<BoxSkeleton sx={{ height: '500px' }} />}
                    errorChildren={<EmptyBox text="Une erreur lors du chargement est survenue" />}
                    isLoading={status === 'idle' || status === 'loading'}
                    isInError={status === 'failed'}
                >
                    {tabs.map((tab, index) => (
                        <TabPanel
                            key={tab.tabName}
                            value={activeTabIndex}
                            index={index}
                        >
                            <Box sx={tabWrapperStyle}>{tab.tabPanel}</Box>
                        </TabPanel>
                    ))}
                </SkeletonLoader>
            </Box>
        );

        if (inAccordion) {
            return (
                <Accordion
                    title={
                        <Box
                            onClick={(e) => e.stopPropagation()}
                            sx={{ width: 'fit-content' }}
                        >
                            {renderTabNavigation()}
                        </Box>
                    }
                    expanded
                    sx={{ backgroundColor: palette.background.paper }}
                    headerSx={{ paddingTop: 1, paddingBottom: 0, borderBottomStyle: 'none' }}
                >
                    {panelContent}
                </Accordion>
            );
        }

        return (
            <>
                {renderTabNavigation()}
                {panelContent}
            </>
        );
    };

    /**
     * Calcule le style du conteneur principal
     */
    const getContainerStyle = (): SxProps => {
        const baseStyle =
            orientation === 'vertical'
                ? {
                      flexGrow: 1,
                      display: 'flex',
                      ...sx,
                  }
                : { ...sx };

        return baseStyle;
    };

    const tabStyle: SxProps = {
        flexGrow: 1,
        bgcolor: palette.background.paper,
        display: 'flex',
        minHeight: '400px',
    };

    return (
        <Box sx={getContainerStyle()}>
            {inCard ? <Card sx={tabStyle}>{renderTabsStructure()}</Card> : renderTabsStructure()}
        </Box>
    );
};
